/**
 * ExecutiveSummary - Displays summary cards for the monthly report.
 *
 * Shows a grid of cards with key metrics:
 * - Vital record count (with breakdown by type)
 * - Feeding record count (with breakdown by meal category)
 * - Output record count (with stool + urine breakdown)
 * - Total notes count
 * - Clinical attention count (highlighted if > 0)
 *
 * Requirements: 5.1, 5.2, 5.3, 5.4, 5.5, 5.6
 */

import React from 'react';

export interface ExecutiveSummaryProps {
  summary: {
    vitalRecordCount: number;
    feedingRecordCount: number;
    outputRecordCount: number;
    stoolCount: number;
    urineCount: number;
    totalNotesCount: number;
    clinicalAttentionCount: number;
    mealBreakdown: {
      breakfast: number;
      lunch: number;
      teaTime: number;
      dinner: number;
      fruits: number;
      snack: number;
    };
    vitalBreakdown: {
      bloodPressure: number;
      heartRate: number;
      temperature: number;
      oxygen: number;
    };
  };
}

const cardStyle: React.CSSProperties = {
  backgroundColor: '#ffffff',
  border: '1px solid #e0e0e0',
  borderRadius: '8px',
  padding: '1rem 1.25rem',
  display: 'flex',
  flexDirection: 'column',
  gap: '0.25rem',
  minWidth: '160px',
};

const cardTitleStyle: React.CSSProperties = {
  fontSize: '0.8rem',
  fontWeight: 600,
  color: '#7f8c8d',
  textTransform: 'uppercase',
  letterSpacing: '0.5px',
  margin: 0,
};

const cardValueStyle: React.CSSProperties = {
  fontSize: '1.75rem',
  fontWeight: 700,
  color: '#2c3e50',
  margin: 0,
};

const cardDetailStyle: React.CSSProperties = {
  fontSize: '0.75rem',
  color: '#95a5a6',
  margin: 0,
  lineHeight: 1.4,
};

const warningCardStyle: React.CSSProperties = {
  ...cardStyle,
  border: '1px solid #e74c3c',
  backgroundColor: '#fdf0ef',
};

const warningValueStyle: React.CSSProperties = {
  ...cardValueStyle,
  color: '#e74c3c',
};

/** Meal category display labels */
const MEAL_LABELS: Record<string, string> = {
  breakfast: 'Breakfast',
  lunch: 'Lunch',
  teaTime: 'Tea Time',
  dinner: 'Dinner',
  fruits: 'Fruits',
  snack: 'Snack',
};

/** Vital type display labels */
const VITAL_LABELS: Record<string, string> = {
  bloodPressure: 'Blood Pressure',
  heartRate: 'Heart Rate',
  temperature: 'Temperature',
  oxygen: 'Oxygen',
};

const ExecutiveSummary: React.FC<ExecutiveSummaryProps> = ({ summary }) => {
  const hasAttention = (summary?.clinicalAttentionCount ?? 0) > 0;

  // Build meal breakdown text (only show categories with count > 0).
  // Guard against missing/null/non-object `mealBreakdown` so the Executive
  // Summary section keeps rendering even when the upstream pipeline omits
  // this field (e.g. all-zero months).
  const mealDetails = Object.entries(summary?.mealBreakdown ?? {})
    .filter(([, count]) => typeof count === 'number' && (count as number) > 0)
    .map(([key, count]) => `${MEAL_LABELS[key] ?? key}: ${count}`)
    .join(' · ');

  // Build vital breakdown text (only show types with count > 0)
  const vitalDetails = Object.entries(summary?.vitalBreakdown ?? {})
    .filter(([, count]) => typeof count === 'number' && (count as number) > 0)
    .map(([key, count]) => `${VITAL_LABELS[key] ?? key}: ${count}`)
    .join(' · ');

  return (
    <section
      className="executive-summary"
      aria-label="Executive Summary"
      style={{ marginBottom: '1.5rem' }}
    >
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))',
          gap: '0.75rem',
        }}
        role="list"
        aria-label="Summary statistics"
      >
        {/* Vital Records */}
        <div style={cardStyle} role="listitem" aria-label={`Vital records: ${summary.vitalRecordCount}`}>
          <p style={cardTitleStyle}>Vital Records</p>
          <p style={cardValueStyle}>{summary.vitalRecordCount}</p>
          {vitalDetails && (
            <p style={cardDetailStyle}>{vitalDetails}</p>
          )}
        </div>

        {/* Feeding Records */}
        <div style={cardStyle} role="listitem" aria-label={`Feeding records: ${summary.feedingRecordCount}`}>
          <p style={cardTitleStyle}>Feeding Records</p>
          <p style={cardValueStyle}>{summary.feedingRecordCount}</p>
          {mealDetails && (
            <p style={cardDetailStyle}>{mealDetails}</p>
          )}
        </div>

        {/* Output Records */}
        <div
          style={cardStyle}
          role="listitem"
          aria-label={`Output records: ${summary.outputRecordCount}, Stool: ${summary.stoolCount}, Urine: ${summary.urineCount}`}
        >
          <p style={cardTitleStyle}>Elimination Log (Output Records)</p>
          <p style={cardValueStyle}>{summary.outputRecordCount}</p>
          <p style={cardDetailStyle}>
            Stool: {summary.stoolCount} · Urine: {summary.urineCount}
          </p>
        </div>

        {/* Total Notes */}
        <div style={cardStyle} role="listitem" aria-label={`Total notes: ${summary.totalNotesCount}`}>
          <p style={cardTitleStyle}>Total Notes</p>
          <p style={cardValueStyle}>{summary.totalNotesCount}</p>
        </div>

        {/* Clinical Attention */}
        <div
          style={hasAttention ? warningCardStyle : cardStyle}
          role="listitem"
          aria-label={`Clinical attention notes: ${summary.clinicalAttentionCount}${hasAttention ? ', requires attention' : ''}`}
        >
          <p style={cardTitleStyle}>
            {hasAttention ? '⚠️ ' : ''}Clinical Attention
          </p>
          <p style={hasAttention ? warningValueStyle : cardValueStyle}>
            {summary.clinicalAttentionCount}
          </p>
        </div>
      </div>
    </section>
  );
};

export default ExecutiveSummary;
